import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Router} from '@angular/router';
import {BehaviorSubject, map, tap, catchError, throwError, Observable, finalize} from 'rxjs';

import {environment} from '../../../environments/environment';
import {GuestTokenService} from './guest-token';
import {SecureStorage} from '../storage/secure-storage';
import {BaseResponse} from '@/shared/models';
import {ApiFeedbackService} from '../api-feedback.service';
import {MAIN_ROUTE} from '../../app.routes';

interface AuthResponseData {
  accessToken: string;
  refreshToken?: string | null;
  expiresAt: number; // timestamp en ms
  isFirstLogin: boolean;
}

export interface MeProfile {
  uuid: string;
  roleUuid?: string | null;
  name: string;
  active: boolean;
  primary: boolean;
  enable: boolean;
  roleName: string;
  rolePrefix: string;
}

@Injectable({providedIn: 'root'})
export class AuthService {
  /** sujeto que mantiene el access token en memoria */
  private access$ = new BehaviorSubject<string | null>(null);
  readonly accessToken$ = this.access$.asObservable();

  /** sujeto para el estado de autenticación */
  private isAuthenticated$ = new BehaviorSubject<boolean>(false);
  readonly authenticated$ = this.isAuthenticated$.asObservable();

  private role$ = new BehaviorSubject<string | null>(null);
  private typeUser$ = new BehaviorSubject<string | null>(null);

  getTypeUser$(): Observable<string | null> {
    return this.typeUser$.asObservable();
  }

  setTypeUser$(role: string): void {
    this.typeUser$.next(role);
  }

  getRole$(): Observable<string | null> {
    return this.role$.asObservable();
  }

  setRole(role: string): void {
    this.role$.next(role);
  }

  constructor(
    private http: HttpClient,
    private guestToken: GuestTokenService,
    private store: SecureStorage,
    private router: Router,
    private feedback: ApiFeedbackService
  ) {
    const saved = this.getAccessToken();
    this.access$.next(saved);
    this.isAuthenticated$.next(this.hasValidToken());
  }

  /**
   * 1) Pide guest-token single-use
   * 2) Llama a /authenticate con form-data y Bearer guest
   * 3) Guarda access token + expiresAt en storage
   * 4) Emite el nuevo token y navega al Home
   */
  login(username: string, password: string): Observable<void> {
    const body = new FormData();
    body.append('username', username);
    body.append('password', password);
    this.feedback.showLoading('Validando credenciales…');

    return this.withSingleUseGuest(guest =>
        this.http.post<BaseResponse<AuthResponseData>>(
          `${environment.apiBase}/auth/authenticate`,
          body,
          {
            withCredentials: true,
            headers: new HttpHeaders({Authorization: `Bearer ${guest}`})
          }
        )
      ).pipe(
      tap(res => {
        this.handleAuthSuccess(res.data);
        this.feedback.showSuccess('Acceso autorizado');
        this.router.navigateByUrl(MAIN_ROUTE);
      }),
      map(() => void 0),
      catchError(err => {
        const serverMsg = err.error?.error    // tu campo "error"
          ?? err.error?.message  // fallback a "message"
          ?? err?.message
          ?? 'Login fallido';
        this.feedback.showError(serverMsg);
        return throwError(() => err);
      })
    );
  }

  finishPasswordReset(data: AuthResponseData): void {
    this.handleAuthSuccess(data);
    this.feedback.showSuccess('Contrasena actualizada');
    this.router.navigateByUrl(MAIN_ROUTE);
  }

  /**
   * Renueva access token usando refresh-token en cookie HttpOnly
   */
  refreshToken(): Observable<BaseResponse<AuthResponseData>> {
    return this.http.post<BaseResponse<AuthResponseData>>(
      `${environment.apiBase}/auth/refresh-token`,
      {},
      {withCredentials: true}
    ).pipe(
      tap(response => {
        this.handleAuthSuccess(response.data);
      })
    );
  }

  getMyProfiles(): Observable<MeProfile[]> {
    return this.http
      .get<BaseResponse<MeProfile[]>>(`${environment.apiBase}/me/profiles`)
      .pipe(
        map(response => (response.data ?? []).map(profile => ({
          uuid: profile?.uuid ?? '',
          roleUuid: profile?.roleUuid ?? null,
          name: profile?.name ?? '',
          active: profile?.active ?? false,
          primary: profile?.primary ?? false,
          enable: profile?.enable ?? false,
          roleName: profile?.roleName ?? '',
          rolePrefix: profile?.rolePrefix ?? ''
        }))),
        catchError(err => {
          const serverMsg = err.error?.error
            ?? err.error?.message
            ?? err?.message
            ?? 'No se pudieron cargar los perfiles';
          this.feedback.showError(serverMsg);
          return throwError(() => err);
        })
      );
  }

  switchProfile(profileUuid: string, rememberAsDefault = false): Observable<void> {
    this.feedback.showLoading('Cambiando perfil...');

    return this.http
      .post<BaseResponse<AuthResponseData>>(
        `${environment.apiBase}/me/profiles/${profileUuid}/switch`,
        {rememberAsDefault}
      )
      .pipe(
        tap(response => {
          this.handleAuthSuccess(response.data);
          this.feedback.showSuccess('Perfil cambiado correctamente');
        }),
        map(() => void 0),
        catchError(err => {
          const serverMsg = err.error?.error
            ?? err.error?.message
            ?? err?.message
            ?? 'No se pudo cambiar de perfil';
          this.feedback.showError(serverMsg);
          return throwError(() => err);
        }),
        finalize(() => this.feedback.hideLoading())
      );
  }

  /**
   * Maneja guardado exitoso de sesión (usado en login, refresh y reset)
   */
  private handleAuthSuccess(data: AuthResponseData): void {
    const {accessToken, expiresAt} = data;
    const accessTtl = Math.max(expiresAt - Date.now(), 1_000);

    // Compat temporal: módulos legacy siguen leyendo 'token'
    this.store.set('access', accessToken, accessTtl);
    this.store.set('token', accessToken, accessTtl);
    this.store.set('expiresAt', expiresAt.toString(), accessTtl);
    this.access$.next(accessToken);
    this.isAuthenticated$.next(true);
  }

  /** Cierra sesión en backend y limpia sesión local */
  logout(): void {
    const accessToken = this.getAccessToken();

    this.feedback.showLoading('Cerrando sesión...');

    const headers = accessToken
      ? new HttpHeaders({Authorization: `Bearer ${accessToken}`})
      : new HttpHeaders();

    this.http.post<BaseResponse<string>>(
      `${environment.apiBase}/auth/logout`,
      {},
      {
        withCredentials: true,
        headers
      }
    ).subscribe({
      next: () => {
        this.feedback.showSuccess('Sesión cerrada correctamente');
        this.clearLocalTokens();
        this.redirectToLogin();
      },
      error: () => {
        this.feedback.showError('No se pudo cerrar sesión en servidor. Se limpió la sesión local.');
        this.clearLocalTokens();
        this.redirectToLogin();
      }
    });
  }

  /** Limpia sesión local y redirige al login */
  handleAuthFailure(): void {
    this.clearLocalTokens();
    this.redirectToLogin();
  }

  redirectToLogin(): void {
    this.router.navigate(['/login']).then(() => {});
  }

  private withSingleUseGuest<T>(fn: (guest: string) => Observable<T>): Observable<T> {
    return this.guestToken.withSingleUseGuest(fn);
  }

  /**
   * 🧹 Limpiar todos los tokens del almacenamiento local
   */
  private clearLocalTokens(): void {
    this.store?.remove('access');
    this.store?.remove('token');
    this.store?.remove('expiresAt');
    this.access$.next(null);
    this.isAuthenticated$.next(false);
  }

  /**
   * 🔍 NUEVOS GETTERS para tokens
   */
  getAccessToken(): string | null {
    return this.store?.get('access') || this.store?.get('token') || null;
  }

  /**
   * ⏰ Verifica si el token está próximo a expirar (5 minutos)
   */
  isTokenExpiringSoon(): boolean {
    if (!this.store) return true;

    const expiresAt = this.store.get('expiresAt');
    if (!expiresAt) return true;

    const expirationTime = parseInt(expiresAt, 10);
    if (Number.isNaN(expirationTime)) return true;

    const currentTime = Date.now();
    const timeUntilExpiry = expirationTime - currentTime;

    // Renovar si expira en menos de 5 minutos (300,000 ms)
    const expiringSoon = timeUntilExpiry < 300000;

    return expiringSoon;
  }

  /**
   * ✅ Verifica si tiene token válido
   */
  private hasValidToken(): boolean {
    if (!this.store) return false;

    const token = this.getAccessToken();
    const expiresAt = this.store.get('expiresAt');

    if (!token || !expiresAt) return false;

    const expiration = parseInt(expiresAt, 10);
    if (Number.isNaN(expiration)) return false;

    return expiration > Date.now();
  }

  /** Atajo para saber si estamos logueados (token válido) */
  isLogged(): boolean {
    return this.hasValidToken();
  }
}
